// Procedural, code-generated model art (no external assets — GDD §7.2). Every squad type
// gets a distinct, readable silhouette; posture states are visually distinct so a
// spectator can read a firefight at a glance. Legion = steel-and-crimson, Kindled =
// ash-and-flame. Facing is rotation-based (models drawn facing +x, then rotated).

import type { Model, Posture, Squad } from "../sim/types";

export interface Palette {
  body: string;
  trim: string;
  steel: string;
  banner: string;
  glow: string;
}

export const LEGION_PAL: Palette = { body: "#4c5560", trim: "#8a1f2b", steel: "#79838f", banner: "#a51c30", glow: "#ffe9b0" };
export const KINDLED_PAL: Palette = { body: "#3a332e", trim: "#c25412", steel: "#5a4f45", banner: "#e0561b", glow: "#ff8a2c" };

interface Pose {
  crouch: number; // 0..1 how low
  lean: number; // forward lean (local +x)
  arm: number; // weapon extension
  prone: boolean;
  run: boolean;
  frenzy: boolean;
}

function poseFor(posture: Posture, phase: number): Pose {
  switch (posture) {
    case "prone":
      return { crouch: 1, lean: 0, arm: 1, prone: true, run: false, frenzy: false };
    case "pinned":
      return { crouch: 0.7, lean: 0, arm: 0.4, prone: false, run: false, frenzy: false };
    case "incover":
      return { crouch: 0.5, lean: 0, arm: 0.5, prone: false, run: false, frenzy: false };
    case "firing":
      return { crouch: 0.12, lean: 0.4 + Math.sin(phase * 20) * 0.06, arm: 1, prone: false, run: false, frenzy: false };
    case "advancing":
      return { crouch: 0.05, lean: 0.5, arm: 0.35, prone: false, run: true, frenzy: false };
    case "broken":
      return { crouch: 0, lean: 0.9, arm: 0.1, prone: false, run: true, frenzy: false };
    case "frenzied":
      return { crouch: 0, lean: 0.7, arm: 0.9, prone: false, run: true, frenzy: true };
    case "melee":
      return { crouch: 0.1, lean: 0.7 + Math.sin(phase * 24) * 0.25, arm: 1, prone: false, run: false, frenzy: false };
    default:
      return { crouch: 0.05, lean: 0, arm: 0.2, prone: false, run: false, frenzy: false };
  }
}

function shadow(ctx: CanvasRenderingContext2D, r: number): void {
  ctx.fillStyle = "rgba(0,0,0,0.28)";
  ctx.beginPath();
  ctx.ellipse(0, r * 0.7, r * 1.1, r * 0.5, 0, 0, Math.PI * 2);
  ctx.fill();
}

function legs(ctx: CanvasRenderingContext2D, p: Pose, w: number, col: string, phase: number): void {
  ctx.strokeStyle = col;
  ctx.lineWidth = w * 0.32;
  ctx.lineCap = "round";
  const spread = p.run ? Math.sin(phase * 16) * w * 0.9 : w * 0.4;
  ctx.beginPath();
  ctx.moveTo(0, w * 0.2);
  ctx.lineTo(-spread * 0.5, w * 1.1);
  ctx.moveTo(0, w * 0.2);
  ctx.lineTo(spread * 0.5, w * 1.1);
  ctx.stroke();
}

/** Generic humanoid trooper facing +x. `s` scales the whole figure (world units). */
function trooper(
  ctx: CanvasRenderingContext2D,
  m: Model,
  pal: Palette,
  s: number,
  opts: { headgear: "helmet" | "hood" | "horns"; weapon: "rifle" | "mg" | "blade" | "flamer" | "launcher" | "crude"; bulk: number; sergeant: boolean; phase: number },
): void {
  const p = poseFor(m.posture, opts.phase + m.bob);
  const bulk = opts.bulk;
  shadow(ctx, s * (0.9 + bulk * 0.25));

  if (p.prone) {
    // flat body lying along +x
    ctx.fillStyle = pal.body;
    ctx.beginPath();
    ctx.ellipse(0, s * 0.5, s * 1.4, s * 0.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = pal.steel;
    ctx.beginPath();
    ctx.arc(s * 1.2, s * 0.5, s * 0.42, 0, Math.PI * 2); // head forward
    ctx.fill();
    // weapon forward
    ctx.strokeStyle = "#2a2622";
    ctx.lineWidth = s * 0.22;
    ctx.beginPath();
    ctx.moveTo(s * 1.1, s * 0.5);
    ctx.lineTo(s * 2.2, s * 0.5);
    ctx.stroke();
    return;
  }

  const yBase = -s * (1 - p.crouch * 0.5);
  legs(ctx, p, s, "#26221e", opts.phase + m.bob);

  // torso (lean forward = shift top +x)
  const lean = p.lean * s;
  ctx.save();
  ctx.translate(0, s * 0.2);
  ctx.fillStyle = pal.body;
  roundRectPath(ctx, -s * 0.55 * (1 + bulk * 0.4), yBase + s * 0.2, s * (1.1 + bulk * 0.5), s * 1.1, s * 0.3);
  ctx.fill();
  // shoulder trim
  ctx.fillStyle = pal.trim;
  roundRectPath(ctx, -s * 0.55 * (1 + bulk * 0.4), yBase + s * 0.2, s * (1.1 + bulk * 0.5), s * 0.34, s * 0.16);
  ctx.fill();
  ctx.restore();

  // head + headgear
  const hx = lean * 0.7;
  const hy = yBase;
  ctx.fillStyle = pal.steel;
  ctx.beginPath();
  ctx.arc(hx, hy, s * 0.44, 0, Math.PI * 2);
  ctx.fill();
  if (opts.headgear === "helmet") {
    ctx.fillStyle = pal.steel;
    roundRectPath(ctx, hx - s * 0.5, hy - s * 0.55, s, s * 0.5, s * 0.18);
    ctx.fill();
    if (opts.sergeant) {
      ctx.fillStyle = pal.banner;
      roundRectPath(ctx, hx - s * 0.12, hy - s * 0.95, s * 0.24, s * 0.5, s * 0.1); // crest
      ctx.fill();
    }
  } else if (opts.headgear === "hood") {
    ctx.fillStyle = pal.body;
    ctx.beginPath();
    ctx.moveTo(hx - s * 0.5, hy + s * 0.3);
    ctx.lineTo(hx, hy - s * 0.7);
    ctx.lineTo(hx + s * 0.5, hy + s * 0.3);
    ctx.closePath();
    ctx.fill();
    // ember eyes
    ctx.fillStyle = pal.glow;
    ctx.beginPath();
    ctx.arc(hx + s * 0.15, hy, s * 0.09, 0, Math.PI * 2);
    ctx.fill();
  } else {
    // horns
    ctx.strokeStyle = "#d9c7a0";
    ctx.lineWidth = s * 0.16;
    ctx.beginPath();
    ctx.moveTo(hx - s * 0.35, hy - s * 0.2);
    ctx.lineTo(hx - s * 0.7, hy - s * 0.8);
    ctx.moveTo(hx + s * 0.35, hy - s * 0.2);
    ctx.lineTo(hx + s * 0.7, hy - s * 0.8);
    ctx.stroke();
  }

  // weapon (from shoulder, pointing +x)
  drawWeapon(ctx, m, pal, s, yBase, p, opts.weapon, opts.phase + m.bob);

  if (p.frenzy) {
    ctx.fillStyle = "rgba(255,120,30,0.5)";
    for (let i = 0; i < 3; i++) {
      const a = opts.phase * 8 + i * 2;
      ctx.beginPath();
      ctx.arc(Math.cos(a) * s * 0.7, yBase + Math.sin(a) * s * 0.6, s * 0.22, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function drawWeapon(ctx: CanvasRenderingContext2D, m: Model, pal: Palette, s: number, yBase: number, p: Pose, kind: string, phase: number): void {
  const sy = yBase + s * 0.55; // shoulder height
  const ext = 0.6 + p.arm * 0.8;
  ctx.save();
  ctx.translate(s * 0.3, sy);
  switch (kind) {
    case "rifle":
      ctx.strokeStyle = "#1f1c19";
      ctx.lineWidth = s * 0.16;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(s * 1.5 * ext, -s * 0.15);
      ctx.stroke();
      break;
    case "crude":
      ctx.strokeStyle = "#2a2622";
      ctx.lineWidth = s * 0.2;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(s * 1.0 * ext, -s * 0.1);
      ctx.stroke();
      break;
    case "mg":
      ctx.strokeStyle = "#14120f";
      ctx.lineWidth = s * 0.26;
      ctx.beginPath();
      ctx.moveTo(-s * 0.2, 0);
      ctx.lineTo(s * 1.9 * ext, -s * 0.1);
      ctx.stroke();
      // bipod
      ctx.lineWidth = s * 0.1;
      ctx.beginPath();
      ctx.moveTo(s * 1.4 * ext, 0);
      ctx.lineTo(s * 1.2 * ext, s * 0.8);
      ctx.moveTo(s * 1.4 * ext, 0);
      ctx.lineTo(s * 1.6 * ext, s * 0.8);
      ctx.stroke();
      break;
    case "launcher":
      ctx.fillStyle = "#2f2a24";
      roundRectPath(ctx, 0, -s * 0.28, s * 1.7 * ext, s * 0.56, s * 0.2);
      ctx.fill();
      ctx.fillStyle = pal.trim;
      ctx.beginPath();
      ctx.arc(s * 1.7 * ext, 0, s * 0.3, 0, Math.PI * 2);
      ctx.fill();
      break;
    case "flamer":
      ctx.strokeStyle = "#2a2622";
      ctx.lineWidth = s * 0.2;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(s * 1.2 * ext, 0);
      ctx.stroke();
      if (m.posture === "firing" || m.muzzleFlash > 0) {
        const grd = ctx.createRadialGradient(s * 1.3 * ext, 0, 0, s * 2.4 * ext, 0, s * 1.6);
        grd.addColorStop(0, "rgba(255,220,120,0.95)");
        grd.addColorStop(0.5, "rgba(255,120,30,0.7)");
        grd.addColorStop(1, "rgba(200,40,10,0)");
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.moveTo(s * 1.2 * ext, -s * 0.5);
        ctx.lineTo(s * 3.0 * ext, 0);
        ctx.lineTo(s * 1.2 * ext, s * 0.5);
        ctx.closePath();
        ctx.fill();
      }
      break;
    case "blade":
      ctx.strokeStyle = "#8b8f96";
      ctx.lineWidth = s * 0.14;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(s * (1.1 + Math.sin(phase * 12) * 0.2), -s * 0.6);
      ctx.stroke();
      ctx.strokeStyle = pal.glow;
      ctx.lineWidth = s * 0.06;
      ctx.stroke();
      break;
  }
  ctx.restore();

  // muzzle flash
  if (m.muzzleFlash > 0 && (kind === "rifle" || kind === "mg" || kind === "crude" || kind === "launcher")) {
    const bx = s * (0.3 + (kind === "mg" ? 1.9 : kind === "launcher" ? 1.7 : 1.3) * ext);
    ctx.fillStyle = "rgba(255,230,150,0.9)";
    ctx.beginPath();
    ctx.arc(bx, sy - s * 0.12, s * 0.3, 0, Math.PI * 2);
    ctx.fill();
  }
}

function firebearerPack(ctx: CanvasRenderingContext2D, m: Model, pal: Palette, s: number, phase: number): void {
  trooper(ctx, m, pal, s, { headgear: "hood", weapon: "flamer", bulk: 0.3, sergeant: m.isSergeant, phase });
  // fuel tank on back
  ctx.fillStyle = "#5a4f45";
  roundRectPath(ctx, -s * 0.9, -s * 0.9, s * 0.5, s * 1.1, s * 0.2);
  ctx.fill();
  ctx.fillStyle = pal.trim;
  ctx.beginPath();
  ctx.arc(-s * 0.65, -s * 0.9, s * 0.18, 0, Math.PI * 2);
  ctx.fill();
}

function vehicle(ctx: CanvasRenderingContext2D, m: Model, pal: Palette, kind: "tank" | "buggy", phase: number): void {
  const s = kind === "tank" ? 15 : 12;
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.beginPath();
  ctx.ellipse(0, s * 0.5, s * 1.9, s * 1.0, 0, 0, Math.PI * 2);
  ctx.fill();
  if (kind === "tank") {
    // treads
    ctx.fillStyle = "#26221e";
    roundRectPath(ctx, -s * 1.7, -s * 0.95, s * 3.4, s * 1.9, s * 0.3);
    ctx.fill();
    // hull
    ctx.fillStyle = pal.body;
    roundRectPath(ctx, -s * 1.4, -s * 0.7, s * 2.8, s * 1.4, s * 0.28);
    ctx.fill();
    ctx.fillStyle = pal.trim;
    roundRectPath(ctx, -s * 1.4, -s * 0.7, s * 0.5, s * 1.4, s * 0.2);
    ctx.fill();
    // turret + barrel
    ctx.fillStyle = pal.steel;
    ctx.beginPath();
    ctx.arc(0, 0, s * 0.7, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#14120f";
    ctx.lineWidth = s * 0.28;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(s * 2.2, 0);
    ctx.stroke();
  } else {
    // buggy: open frame + spikes + exhaust
    ctx.fillStyle = "#2b2620";
    roundRectPath(ctx, -s * 1.5, -s * 0.5, s * 3.0, s * 1.0, s * 0.2);
    ctx.fill();
    ctx.fillStyle = pal.trim;
    roundRectPath(ctx, s * 0.2, -s * 0.4, s * 1.1, s * 0.8, s * 0.15);
    ctx.fill();
    // wheels
    ctx.fillStyle = "#161310";
    for (const wx of [-s * 1.1, s * 1.1]) {
      ctx.beginPath();
      ctx.arc(wx, s * 0.55, s * 0.45, 0, Math.PI * 2);
      ctx.fill();
    }
    // spikes
    ctx.strokeStyle = "#8b8f96";
    ctx.lineWidth = s * 0.12;
    ctx.beginPath();
    ctx.moveTo(s * 1.3, 0);
    ctx.lineTo(s * 2.0, 0);
    ctx.stroke();
    // exhaust flame
    const fl = 0.7 + Math.sin(phase * 18) * 0.3;
    ctx.fillStyle = "rgba(255,140,40,0.8)";
    ctx.beginPath();
    ctx.moveTo(-s * 1.5, -s * 0.2);
    ctx.lineTo(-s * (2.0 + fl), 0);
    ctx.lineTo(-s * 1.5, s * 0.2);
    ctx.closePath();
    ctx.fill();
  }
  // gun muzzle flash
  if (m.muzzleFlash > 0) {
    ctx.fillStyle = "rgba(255,230,150,0.9)";
    ctx.beginPath();
    ctx.arc(s * (kind === "tank" ? 2.2 : 2.0), 0, s * 0.4, 0, Math.PI * 2);
    ctx.fill();
  }
}

function hero(ctx: CanvasRenderingContext2D, m: Model, pal: Palette, faction: "legion" | "kindled", phase: number): void {
  const s = 12;
  // aura ring
  const auraCol = faction === "legion" ? "rgba(255,215,120,0.16)" : "rgba(255,110,30,0.18)";
  ctx.fillStyle = auraCol;
  ctx.beginPath();
  ctx.arc(0, 0, s * 2.6 + Math.sin(phase * 4) * s * 0.2, 0, Math.PI * 2);
  ctx.fill();
  // cape / banner
  ctx.fillStyle = pal.banner;
  ctx.beginPath();
  ctx.moveTo(-s * 0.3, -s * 1.4);
  ctx.lineTo(-s * 1.3, s * 1.2);
  ctx.lineTo(-s * 0.1, s * 0.9);
  ctx.closePath();
  ctx.fill();
  trooper(ctx, m, pal, s, { headgear: faction === "legion" ? "helmet" : "horns", weapon: faction === "legion" ? "blade" : "blade", bulk: 0.5, sergeant: true, phase });
  // glowing weapon overlay
  ctx.strokeStyle = faction === "legion" ? "#fff2c0" : "#ff9a3c";
  ctx.lineWidth = s * 0.14;
  ctx.beginPath();
  ctx.moveTo(s * 0.6, -s * 0.2);
  ctx.lineTo(s * 2.0, -s * 1.2);
  ctx.stroke();
}

export function drawModel(ctx: CanvasRenderingContext2D, sq: Squad, m: Model, pos: { x: number; y: number }, facing: number, pal: Palette, phase: number): void {
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(facing);
  const sil = sq.def.silhouette;
  const launcher = !!m.weaponOverride;
  switch (sil) {
    case "line":
      trooper(ctx, m, pal, 8, { headgear: "helmet", weapon: launcher ? "launcher" : "rifle", bulk: 0, sergeant: m.isSergeant, phase });
      break;
    case "heavy":
      trooper(ctx, m, pal, 8.5, { headgear: "helmet", weapon: "mg", bulk: 0.25, sergeant: m.isSergeant, phase });
      break;
    case "assault":
      trooper(ctx, m, pal, 9, { headgear: "helmet", weapon: "blade", bulk: 0.5, sergeant: m.isSergeant, phase });
      break;
    case "zealot":
      trooper(ctx, m, pal, 7, { headgear: "hood", weapon: launcher ? "flamer" : "crude", bulk: 0, sergeant: m.isSergeant, phase });
      break;
    case "firebearer":
      firebearerPack(ctx, m, pal, 8, phase);
      break;
    case "shock":
      trooper(ctx, m, pal, 9, { headgear: "horns", weapon: "blade", bulk: 0.4, sergeant: m.isSergeant, phase });
      break;
    case "tank":
      vehicle(ctx, m, pal, "tank", phase);
      break;
    case "buggy":
      vehicle(ctx, m, pal, "buggy", phase);
      break;
    case "hero_legion":
      hero(ctx, m, pal, "legion", phase);
      break;
    case "hero_kindled":
      hero(ctx, m, pal, "kindled", phase);
      break;
    default:
      trooper(ctx, m, pal, 8, { headgear: "helmet", weapon: "rifle", bulk: 0, sergeant: false, phase });
  }
  ctx.restore();
}

// ---- small helpers ---------------------------------------------------------

export function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
